import React, { createContext, useState, useContext, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';

// User type definition
interface User {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  is_advanced_user: boolean;
  is_expert: boolean;
  // Gates the billed Haiku vision cross-check on launch-monitor imports.
  // Optional because tokens issued before the column existed omit it; treat a
  // missing value as false rather than granting a paid call by default.
  is_advanced_haiku?: boolean;
  // Player's Secret Corner pass. Optional for the same reason as is_advanced_haiku —
  // tokens issued before the column existed omit it.
  is_secret_corner?: boolean;
  last_login: string | null;
  created_at: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  country?: string | null;
  state?: string | null;
}

// Auth context type definition
interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  clearSessionExpired: () => void;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (email: string, password: string, fullName: string, demographics?: { dateOfBirth?: string; gender?: string; country?: string }) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (resetToken: string, newPassword: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Decode JWT to get expiry timestamp (no library needed)
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ?? null;
  } catch {
    return null;
  }
}

// Which storage to use (persisted in localStorage so we know on next load)
function getAuthStorage(): Storage {
  const type = localStorage.getItem('auth_storage_type');
  return type === 'session' ? sessionStorage : localStorage;
}

// Props for AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Refs so interceptors / timers don't capture stale state
  const refreshTokenRef = useRef<string | null>(null);
  const isRefreshingRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load auth state from storage on mount
  useEffect(() => {
    const storage = getAuthStorage();
    const storedToken = storage.getItem('access_token');
    const storedRefreshToken = storage.getItem('refresh_token');
    const storedUser = storage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setRefreshToken(storedRefreshToken);
      refreshTokenRef.current = storedRefreshToken;
      setUser(JSON.parse(storedUser));
      scheduleTokenRefresh(storedToken);
    }

    setIsLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Configure axios to include token in all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Schedule a proactive token refresh before expiry
  const scheduleTokenRefresh = (accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const exp = getTokenExpiry(accessToken);
    if (!exp) return;
    const msUntilExpiry = exp * 1000 - Date.now();
    // Refresh 2 minutes before expiry (or immediately if already close)
    const refreshIn = Math.max(msUntilExpiry - 2 * 60 * 1000, 0);
    refreshTimerRef.current = setTimeout(async () => {
      if (refreshTokenRef.current) {
        try {
          await doRefresh(refreshTokenRef.current);
        } catch {
          // Refresh failed — mark as expired
          setSessionExpired(true);
          doLogout();
        }
      }
    }, refreshIn);
  };

  // Internal refresh (doesn't rely on state — uses ref)
  const doRefresh = async (rt: string) => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
        refresh_token: rt,
      });
      const { access_token, refresh_token: new_refresh_token, user: userData } = response.data;
      const storage = getAuthStorage();
      storage.setItem('access_token', access_token);
      storage.setItem('refresh_token', new_refresh_token);
      storage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setRefreshToken(new_refresh_token);
      refreshTokenRef.current = new_refresh_token;
      setUser(userData);
      scheduleTokenRefresh(access_token);
    } finally {
      isRefreshingRef.current = false;
    }
  };

  const doLogout = () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const storage = getAuthStorage();
    storage.removeItem('access_token');
    storage.removeItem('refresh_token');
    storage.removeItem('user');
    // Only clear the storage-type marker on explicit logout, not on expiry
    setToken(null);
    setRefreshToken(null);
    refreshTokenRef.current = null;
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Listen for session-expired events from other axios instances (e.g. recommendationService)
  useEffect(() => {
    const handler = () => {
      if (refreshTokenRef.current && !isRefreshingRef.current) {
        doRefresh(refreshTokenRef.current).catch(() => {
          setSessionExpired(true);
          doLogout();
        });
      } else if (!refreshTokenRef.current) {
        setSessionExpired(true);
        doLogout();
      }
    };
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up global 401 interceptor once
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Skip if this is the refresh call itself (avoid infinite loop)
        if (
          error.response?.status === 401 &&
          !error.config?._retried &&
          !error.config?.url?.includes('/auth/refresh')
        ) {
          error.config._retried = true;
          const rt = refreshTokenRef.current;
          if (rt && !isRefreshingRef.current) {
            try {
              await doRefresh(rt);
              // Retry original request with new token
              const storage = getAuthStorage();
              const newToken = storage.getItem('access_token');
              error.config.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(error.config);
            } catch {
              setSessionExpired(true);
              doLogout();
            }
          } else {
            // No refresh token — session expired
            setSessionExpired(true);
            doLogout();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Login function
  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/login`, {
        email,
        password,
      });

      const { access_token, refresh_token, user: userData } = response.data;

      // Choose storage based on rememberMe
      const storage = rememberMe ? localStorage : sessionStorage;
      localStorage.setItem('auth_storage_type', rememberMe ? 'local' : 'session');

      storage.setItem('access_token', access_token);
      storage.setItem('refresh_token', refresh_token);
      storage.setItem('user', JSON.stringify(userData));

      setToken(access_token);
      setRefreshToken(refresh_token);
      refreshTokenRef.current = refresh_token;
      setUser(userData);
      setSessionExpired(false);
      scheduleTokenRefresh(access_token);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  // Signup function
  const signup = async (email: string, password: string, fullName: string, demographics?: { dateOfBirth?: string; gender?: string; country?: string }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/register`, {
        email,
        password,
        full_name: fullName,
        ...(demographics?.dateOfBirth && { date_of_birth: demographics.dateOfBirth }),
        ...(demographics?.gender && { gender: demographics.gender }),
        ...(demographics?.country && { country: demographics.country }),
      });

      const { access_token, refresh_token, user: userData } = response.data;

      // Signup defaults to session storage (user can set remember me on next login)
      localStorage.setItem('auth_storage_type', 'session');
      sessionStorage.setItem('access_token', access_token);
      sessionStorage.setItem('refresh_token', refresh_token);
      sessionStorage.setItem('user', JSON.stringify(userData));

      setToken(access_token);
      setRefreshToken(refresh_token);
      refreshTokenRef.current = refresh_token;
      setUser(userData);
      setSessionExpired(false);
      scheduleTokenRefresh(access_token);
    } catch (error: any) {
      console.error('Signup error:', error);
      throw new Error(error.response?.data?.detail || 'Signup failed');
    }
  };

  // Request a password-reset email. Backend always returns 200 (even for an
  // unregistered email) to avoid leaking which emails exist, so this never
  // throws on a "not found" case — only on a network/server failure.
  const requestPasswordReset = async (email: string) => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/auth/forgot-password`, { email });
    } catch (error: any) {
      console.error('Forgot-password error:', error);
      throw new Error(error.response?.data?.detail || 'Could not send reset email. Please try again.');
    }
  };

  // Set a new password using the token from the reset email.
  const resetPassword = async (resetToken: string, newPassword: string) => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword,
      });
    } catch (error: any) {
      console.error('Reset-password error:', error);
      throw new Error(error.response?.data?.detail || 'Could not reset password. The link may have expired.');
    }
  };

  // Logout function (explicit)
  const logout = () => {
    localStorage.removeItem('auth_storage_type');
    doLogout();
  };

  // Public refresh (called externally)
  const refreshAccessToken = async () => {
    if (!refreshTokenRef.current) {
      logout();
      return;
    }
    try {
      await doRefresh(refreshTokenRef.current);
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  // Refresh user data (after profile updates)
  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = response.data;
      const storage = getAuthStorage();
      storage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      console.error('User refresh failed:', error);
    }
  };

  const clearSessionExpired = () => setSessionExpired(false);

  const value = {
    user,
    token,
    refreshToken,
    isAuthenticated: !!user && !!token,
    isLoading,
    sessionExpired,
    clearSessionExpired,
    login,
    signup,
    requestPasswordReset,
    resetPassword,
    logout,
    refreshAccessToken,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
